import useCustomerLocations, {
  CustomerLocationDTO,
} from 'src/api/accounts-receivable/customers/useCustomerLocations';
import { Autocomplete } from '@/components/common/inputs/autocomplete';
import { Error } from '@/components/common/semantic-icons';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import LocationPill from 'src/components/location/LocationPill';

function LocationItem({
  location,
  isTrigger = false,
}: {
  location: CustomerLocationDTO;
  isTrigger?: boolean;
}) {
  return (
    <div className="flex items-center gap-1">
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium">
          {location.name || 'Unnamed Location'}
        </span>
        {!isTrigger && (
          <span className="text-xs text-muted-foreground">
            {location.address || 'No address provided'}
          </span>
        )}
        {!isTrigger && location.dunsNumber && (
          <span className="text-xs text-muted-foreground">
            DUNS: {location.dunsNumber}
          </span>
        )}
      </div>
    </div>
  );
}

export function LocationPicker({
  onLocationSelect,
  selectedLocationId,
  customerId,
  className,
  error,
  placeholder = 'Select a location',
  emptyMessage = 'No locations found for this customer',
  siteUseCode,
}: {
  onLocationSelect: (locationId?: string | undefined) => void;
  selectedLocationId?: string;
  customerId: string;
  className?: string;
  error?: string;
  placeholder?: string;
  emptyMessage?: string;
  siteUseCode?: 'SHIP_TO' | 'BILL_TO';
}) {
  const [searchValue, setSearchValue] = useState('');

  const {
    data: locationsData,
    isLoading: isLoadingLocations,
    error: locationsLoadError,
  } = useCustomerLocations(customerId, !!customerId);

  const locations: CustomerLocationDTO[] = locationsData?.locations || [];

  // Custom filter function to handle null values safely
  const filterFunction = (location: CustomerLocationDTO, search: string) => {
    if (!search) return true;

    const searchLower = search.toLowerCase();
    const name = location.name || '';
    const address = location.address || '';
    const id = location.id || '';

    return (
      name.toLowerCase().includes(searchLower) ||
      address.toLowerCase().includes(searchLower) ||
      id.toLowerCase().includes(searchLower)
    );
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className={cn(error && 'ring-2 ring-red-500 rounded-md p-1')}>
        <Autocomplete<CustomerLocationDTO>
          options={locations}
          value={selectedLocationId || undefined}
          disabled={isLoadingLocations}
          placeholder={placeholder}
          getValue={(location: CustomerLocationDTO) => location.id || ''}
          getLabel={(location: CustomerLocationDTO) =>
            location.name || 'Unnamed Location'
          }
          renderOption={(location: CustomerLocationDTO) => (
            <LocationItem location={location} />
          )}
          renderTrigger={(location: CustomerLocationDTO) => (
            <LocationPill
              location={{
                name: location.name,
                address: location.address,
              }}
            />
          )}
          searchValue={searchValue}
          onSearchValueChange={setSearchValue}
          onValueChange={onLocationSelect}
          multiSelect={false}
          filterFunction={filterFunction}
        />
      </div>
      {locationsLoadError && (
        <div className="flex items-center gap-1">
          <Error />
          <span className="text-sm text-muted-foreground">
            Failed to load customer locations: {locationsLoadError.message}
          </span>
        </div>
      )}
      {!isLoadingLocations && locations.length === 0 && (
        <div className="flex items-center gap-1">
          <span className="text-sm text-muted-foreground">{emptyMessage}</span>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-1">
          <Error />
          <span className="text-sm text-red-500">{error}</span>
        </div>
      )}
    </div>
  );
}
